// In-chat "what this connector brought you" card — pure helpers shared by the chat state
// (use-chat), the BFF history mapping (messages API), and the card UI
// (MessageRow/PostConnectedConnectorCard). The wire shape is the harness contract: the ASSISTANT
// reply of a turn whose ShowConnectorCard tool ran carries `metadata.postConnectedConnectorCard`,
// and the SSE `messageEnd` event carries the same card in-band, like `choices`.
//
// Deliberately dependency-free (mirrors lib/connector-card.ts and lib/opportunity-card.ts): unit
// tests must not drag the common-lib graph, so the validation here MIRRORS common-lib's
// `PostConnectedConnectorCardMetadata` rather than importing it. Keep the two in step.

export interface PostConnectedConnectorCardData {
  toolkit: string;
  name: string;
  logoUrl?: string;
  /**
   * NOT here: the routines. The card reads them live from `useRoutines()`, filtered to this `toolkit`.
   * The rows are switches, and a switch drawn from the turn's snapshot shows the wrong state the moment
   * anything else moves it — the Routines screen, another device, the agent itself.
   */
  /** The kit's widgets now on the home page, by slug — the card draws the count. Absent when it put none there. */
  homeWidgets?: string[];
}

/**
 * Read the card off a message's metadata, or `null` when the row is not one.
 *
 * Fails closed only on the two fields the card cannot render without: they title it and pick its logo.
 * Everything else is optional, and the switch rows are not here at all — the card queries them.
 */
export function readPostConnectedConnectorCard(metadata: unknown): PostConnectedConnectorCardData | null {
  const card = (metadata as { postConnectedConnectorCard?: unknown } | null | undefined)?.postConnectedConnectorCard as
    | Partial<PostConnectedConnectorCardData>
    | undefined;
  if (!card || typeof card.toolkit !== 'string' || !card.toolkit.trim() || typeof card.name !== 'string' || !card.name.trim()) {
    return null;
  }
  const homeWidgets = readHomeWidgets((card as { homeWidgets?: unknown }).homeWidgets);
  return {
    toolkit: card.toolkit,
    name: card.name,
    ...(typeof card.logoUrl === 'string' && card.logoUrl ? { logoUrl: card.logoUrl } : {}),
    ...(homeWidgets.length > 0 ? { homeWidgets } : {}),
  };
}

/** The home widgets' slugs, dropping anything that is not a usable one — the count has to be of real widgets. */
function readHomeWidgets(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.filter((slug): slug is string => typeof slug === 'string' && Boolean(slug.trim())) : [];
}
